/**
 * Composable for detecting version changes by comparing script hashes
 *
 * Compares the current page's script tags against freshly fetched HTML
 * to detect when a new deployment has occurred.
 */

/**
 * Extracts the hash portion from a Vite chunk filename
 * Example: "assets/index-AbCd1234.js" -> "AbCd1234"
 */
function extractHashFromFilename(filename: string): string | null {
  // Match pattern: name-HASH.ext (e.g., index-AbCd1234.js)
  const hashMatch = filename.match(/-([A-Za-z0-9]{8,})\./)
  return hashMatch ? hashMatch[1] ?? null : null
}

/**
 * Gets the main script hash from the current page's DOM
 */
function getCurrentScriptHash(): string | null {
  // Find the main entry script (usually contains "index" in the path)
  const scripts = Array.from(document.querySelectorAll('script[src*="assets/"]'))

  for (const script of scripts) {
    const src = script.getAttribute('src')
    if (src && src.includes('index')) {
      const hash = extractHashFromFilename(src)
      if (hash) return hash
    }
  }

  // Fallback: use the first script with a hash
  for (const script of scripts) {
    const src = script.getAttribute('src')
    if (src) {
      const hash = extractHashFromFilename(src)
      if (hash) return hash
    }
  }

  return null
}

/**
 * Fetches the latest HTML and extracts the main script hash
 *
 * Returns null on any error to avoid breaking the app
 */
async function fetchLatestScriptHash(): Promise<string | null> {
  try {
    // Cache-bust to always get fresh HTML
    const response = await fetch(`/?_v=${Date.now()}`, {
      method: 'GET',
      headers: {
        Accept: 'text/html'
      },
      credentials: 'omit',
      cache: 'no-store'
    })

    if (!response.ok) {
      return null
    }

    const html = await response.text()

    // Extract script src from HTML using regex
    // Looking for: <script ... src="/assets/index-HASH.js" ...>
    const scriptMatches = html.matchAll(/src="([^"]*assets[^"]*index[^"]*\.js)"/g)

    for (const match of scriptMatches) {
      const src = match[1]!
      const hash = extractHashFromFilename(src)
      if (hash) return hash
    }

    // Fallback: any script in assets folder
    const fallbackMatches = html.matchAll(/src="([^"]*assets[^"]*\.js)"/g)
    for (const match of fallbackMatches) {
      const src = match[1]!
      const hash = extractHashFromFilename(src)
      if (hash) return hash
    }

    return null
  } catch {
    // Network error, parse error, etc.
    // Silently fail - version checking is optional functionality
    return null
  }
}

/**
 * Fetches the version hash for comparison
 *
 * On first call (initialization), returns hash from current DOM
 * On subsequent calls (polling), fetches fresh HTML to compare
 */
export async function fetchManifestHash(): Promise<string | null> {
  // For polling, fetch latest from server
  return await fetchLatestScriptHash()
}

/**
 * Gets the initial hash from the current page (call once at startup)
 */
export function getInitialHash(): string | null {
  return getCurrentScriptHash()
}

/**
 * Compares two version hashes
 * Returns true if they differ (new version available)
 */
export function hasVersionChanged(
  currentHash: string | null,
  latestHash: string | null
): boolean {
  // Can't compare if either is null
  if (!currentHash || !latestHash) return false

  return currentHash !== latestHash
}
